import { useAuthStore } from '@/stores/authStore';
import { UserRole, User, LoginCredentials, isStaffRole } from '@/types/auth';
import { isEntrepreneurPath } from '@/utils/paths';

// Base authentication hook
export const useBaseAuth = (role?: UserRole) => {
  const store = useAuthStore();
  
  // Role-specific filtering
  const isCurrentRole = role ? store.isRole(role) : true;
  
  return {
    ...store,
    // Override authentication status based on role filter
    isAuthenticated: store.isAuthenticated && isCurrentRole,
    // Convenience methods with role pre-filled
    logout: () => store.logout(role),
    redirectToLogin: role ?
      (redirectPath?: string) => store.redirectToLogin(role, redirectPath) :
      store.redirectToLogin,
    redirectToDashboard: role ?
      (fallbackPath?: string) => store.redirectToDashboard(role, fallbackPath) :
      store.redirectToDashboard,
  };
};

// Admin-specific authentication hook
export const useAdminAuth = () => {
  const store = useAuthStore();
  const isStaff = isStaffRole(store.user?.role);

  return {
    ...store,
    isAuthenticated: store.isAuthenticated && isStaff,
    logout: () => store.logout(),
    redirectToLogin: store.redirectToLogin,
    redirectToDashboard: store.redirectToDashboard,
    adminUser: isStaff ? store.user : null,
    isAdmin: isStaff,
  };
};

// Entrepreneur-specific authentication hook  
export const useEntrepreneurAuth = () => {
  const auth = useBaseAuth(UserRole.ENTREPRENEUR);
  
  const entrepreneurUser = auth.user?.role === UserRole.ENTREPRENEUR ? 
    auth.user as any : null; // Type assertion since we know it's entrepreneur
  
  const hasAcceptedConsent = !!(entrepreneurUser?.terms_accepted_date && entrepreneurUser?.privacy_accepted_date);

  return {
    ...auth,
    entrepreneurUser,
    isEntrepreneur: auth.isRole(UserRole.ENTREPRENEUR),
    hasAcceptedConsent,
    // Entrepreneur-specific state helpers
    isPendingLinkedInParse: entrepreneurUser?.entrepreneur_state === 'PENDING_LINKEDIN_PARSE',
    isPendingCvUpload: entrepreneurUser?.entrepreneur_state === 'PENDING_CV_UPLOAD',
    isPendingOnboarding: entrepreneurUser?.entrepreneur_state === 'PENDING_ONBOARDING',
    isPending: entrepreneurUser?.entrepreneur_state && [
      'PENDING_LINKEDIN_PARSE',
      'PENDING_CV_UPLOAD',
      'PENDING_ONBOARDING',
      'PENDING_SUMMARY',
      'PENDING_PASSED_PRESCORE',
      'PENDING_FAILED_PRESCORE',
      'PENDING_WITH_NOTES'
    ].includes(entrepreneurUser.entrepreneur_state),
    isApproved: entrepreneurUser?.entrepreneur_state === 'APPROVED',
    isRejected: entrepreneurUser?.entrepreneur_state === 'REJECTED',
    // Entrepreneur-specific methods
    submitProfile: async (profileData?: any) => {
      const { callApi } = await import('@/hooks/useFetch');

      const response = await callApi('/entrepreneur/profile/submit', {
        method: 'POST',
        data: profileData
      });

      // Refresh auth to get updated user state
      auth.refreshAuth();

      return response;
    },
    updateProfile: async (profileData: Record<string, unknown>) => {
      const { callApi } = await import('@/hooks/useFetch');

      await callApi('/entrepreneur/profile/update', {
        method: 'PUT',
        data: { profile_data: profileData }
      });

      auth.refreshAuth();
    },
  };
};

// Legacy compatibility - uses global auth without role filtering
export const useAuth = () => {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const isLoading = useAuthStore(s => s.isLoading);
  const error = useAuthStore(s => s.error);
  const logout = useAuthStore(s => s.logout);
  const refreshAuth = useAuthStore(s => s.refreshAuth);
  const loginWithCredentials = useAuthStore(s => s.loginWithCredentials);

  return { user, isAuthenticated, isLoading, error, logout, refreshAuth, loginWithCredentials, isLoggingOut: false };
};

// Hook for role-agnostic authentication (detects role from current route)
export const useAutoRoleAuth = () => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const onEntrepreneurPath = isEntrepreneurPath(path);

  // Entrepreneur paths get role-filtered auth; staff paths get unfiltered (any staff role passes).
  // Called as a single hook invocation so React's hook ordering rule isn't violated.
  return useBaseAuth(onEntrepreneurPath ? UserRole.ENTREPRENEUR : undefined);
};

// Hook for authentication without role restrictions (system-wide)
export const useGlobalAuth = () => useBaseAuth();

// Hook for checking authentication status across multiple roles
export const useMultiRoleAuth = (roles: UserRole[]) => {
  const auth = useBaseAuth();
  
  const hasAnyRole = roles.some(role => auth.isRole(role));
  
  return {
    ...auth,
    isAuthenticated: auth.isAuthenticated && hasAnyRole,
    hasAnyRole,
    allowedRoles: roles,
  };
};

// Hook for authentication actions only (no state)
export const useAuthActions = () => {
  const store = useAuthStore();
  
  return {
    loginWithCredentials: store.loginWithCredentials,
    logout: store.logout,
    refreshAuth: store.refreshAuth,
    setUser: store.setUser,
    clearCredentials: store.clearCredentials,
    handleAuthRedirect: store.handleAuthRedirect,
    redirectToLogin: store.redirectToLogin,
    redirectToDashboard: store.redirectToDashboard,
  };
};

// Hook for authentication state only (no actions)
export const useAuthState = () => {
  const store = useAuthStore();
  
  return {
    user: store.user,
    permissions: store.permissions,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
    error: store.error,
    currentRole: store.currentRole,
    isRole: store.isRole,
    hasPermission: store.hasPermission,
  };
};